import React, { useEffect, useRef, useState } from "react";
import { MdPhone, MdEmail } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../../store/slices/profile";
import {
  sendChangeMobileOtp,
  verifyChangeMobileOtp,
  sendChangeEmailOtp,
  verifyChangeEmailOtp,
} from "../../store/slices/auth";
import { Modal, Form } from "react-bootstrap";
import { toast } from "react-toastify";

export default function AccountSettings() {
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);

  // mobile / email
  const [changeType, setChangeType] = useState("mobile");

  // step 1 = enter value
  // step 2 = enter OTP
  const [step, setStep] = useState(1);

  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");

  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const otpRefs = useRef([]);

  const {
    profilelist: profile = {},
    loading,
  } = useSelector((state) => state.profile);

  useEffect(() => {
    dispatch(getProfile());
  }, [dispatch]);

  /* ==========================================
     MASK MOBILE
  ========================================== */

  const maskMobile = (mobile = "") => {
    if (!mobile) return "Not Available";

    if (mobile.length <= 4) return mobile;

    return `${mobile.slice(0, 2)}******${mobile.slice(-2)}`;
  };

  /* ==========================================
     MASK EMAIL
  ========================================== */

  const maskEmail = (email = "") => {
    if (!email) return "Not Available";

    const [name, domain] = email.split("@");

    if (!domain) return email;

    if (name.length <= 2) {
      return `${name}****@${domain}`;
    }

    return `${name.slice(0, 2)}****@${domain}`;
  };

  /* ==========================================
     OPEN MOBILE MODAL
  ========================================== */

  const openMobileModal = () => {
    setChangeType("mobile");
    setMobileNumber("");
    setOtp("");
    setStep(1);
    setSending(false);
    setVerifying(false);
    otpRefs.current = [];

    setShowModal(true);
  };

  /* ==========================================
     OPEN EMAIL MODAL
  ========================================== */

  const openEmailModal = () => {
    setChangeType("email");
    setEmail("");
    setOtp("");
    setStep(1);
    setSending(false);
    setVerifying(false);
    otpRefs.current = [];

    setShowModal(true);
  };

  /* ==========================================
     CLOSE MODAL
  ========================================== */

  const closeModal = () => {
    if (sending || verifying) return;

    setShowModal(false);
    setStep(1);
    setOtp("");
    setMobileNumber("");
    setEmail("");
    otpRefs.current = [];
  };

  /* ==========================================
     SEND MOBILE OTP
  ========================================== */

  const handleSendMobileOtp = async () => {
    if (!/^[6-9]\d{9}$/.test(mobileNumber)) {
      return toast.error("Enter a valid mobile number");
    }

    try {
      setSending(true);

      await dispatch(
        sendChangeMobileOtp({
          mobileNumber,
        })
      ).unwrap();

      toast.success("OTP sent successfully");

      setOtp("");
      setStep(2);

    } catch (err) {
      toast.error(
        typeof err === "string"
          ? err
          : "Failed to send OTP"
      );
    } finally {
      setSending(false);
    }
  };

  /* ==========================================
     VERIFY MOBILE OTP
  ========================================== */

  const handleVerifyMobileOtp = async () => {
    if (otp.length !== 6) {
      return toast.error("Enter valid OTP");
    }

    try {
      setVerifying(true);

      const res = await dispatch(
        verifyChangeMobileOtp({
          mobileNumber,
          otp,
        })
      ).unwrap();

      toast.success(
        res?.message ||
        "Mobile number updated successfully"
      );

      await dispatch(getProfile());

      closeModal();

    } catch (err) {
      toast.error(
        typeof err === "string"
          ? err
          : "OTP verification failed"
      );
    } finally {
      setVerifying(false);
    }
  };

  /* ==========================================
     SEND EMAIL OTP
  ========================================== */

  const handleSendEmailOtp = async () => {
    const normalizedEmail = email.trim().toLowerCase();

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(normalizedEmail)) {
      return toast.error("Enter a valid email address");
    }

    try {
      setSending(true);

      await dispatch(
        sendChangeEmailOtp({
          email: normalizedEmail,
        })
      ).unwrap();

      toast.success(
        "OTP sent successfully to your new email"
      );

      setEmail(normalizedEmail);
      setOtp("");
      setStep(2);

    } catch (err) {
      toast.error(
        typeof err === "string"
          ? err
          : "Failed to send OTP"
      );
    } finally {
      setSending(false);
    }
  };

  /* ==========================================
     VERIFY EMAIL OTP
  ========================================== */

  const handleVerifyEmailOtp = async () => {
    if (otp.length !== 6) {
      return toast.error("Enter valid OTP");
    }

    try {
      setVerifying(true);

      const res = await dispatch(
        verifyChangeEmailOtp({
          email,
          otp,
        })
      ).unwrap();

      toast.success(
        res?.message ||
        "Email updated successfully"
      );

      await dispatch(getProfile());

      closeModal();

    } catch (err) {
      toast.error(
        typeof err === "string"
          ? err
          : "OTP verification failed"
      );
    } finally {
      setVerifying(false);
    }
  };

  /* ==========================================
     SEND OTP
  ========================================== */

  const handleSendOtp = async () => {
    if (changeType === "mobile") {
      await handleSendMobileOtp();
    } else {
      await handleSendEmailOtp();
    }
  };

  /* ==========================================
     VERIFY OTP
  ========================================== */

  const handleVerifyOtp = async () => {
    if (changeType === "mobile") {
      await handleVerifyMobileOtp();
    } else {
      await handleVerifyEmailOtp();
    }
  };

  /* ==========================================
     OTP INPUT
  ========================================== */

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");

    if (!value) return;

    const newOtp = otp.split("");

    newOtp[index] = value.charAt(0);

    setOtp(newOtp.join(""));

    if (index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = otp.split("");

      newOtp[index] = "";

      setOtp(newOtp.join(""));

      if (
        index > 0 &&
        !otp[index]
      ) {
        otpRefs.current[index - 1]?.focus();
      }
    }
  };

  return (
    <>
      {/* ==========================================
          ACCOUNT SETTINGS
      ========================================== */}

      <div className="ep-section mt-5">

        <div className="ep-sec-header">

          <div className="ep-sec-left">

            <div
              className="ep-sec-icon"
              style={{
                background: "#eef4ff",
              }}
            >
              ⚙️
            </div>

            <div>

              <div className="ep-sec-title">
                Account Settings
              </div>

              <div className="ep-sec-subtitle">
                Manage your account security
              </div>

            </div>

          </div>

        </div>

        <div className="account-settings">

          {/* ==========================================
              MOBILE
          ========================================== */}

          <div className="account-item">

            <div className="account-left">

              <div className="account-icon">
                <MdPhone />
              </div>

              <div>

                <div className="account-title">
                  Mobile Number
                </div>

                <div className="account-value">

                  {loading
                    ? "Loading..."
                    : maskMobile(
                      profile.mobileNumber
                    )}

                </div>

              </div>

            </div>

            <button
              className="ep-btn-save"
              onClick={openMobileModal}
            >
              Change
            </button>

          </div>

          {/* ==========================================
              EMAIL
          ========================================== */}

          <div className="account-item">

            <div className="account-left">

              <div className="account-icon">
                <MdEmail />
              </div>

              <div>

                <div className="account-title">
                  Email
                </div>

                <div className="account-value">

                  {loading
                    ? "Loading..."
                    : maskEmail(
                      profile.email
                    )}

                </div>

              </div>

            </div>

            <button
              className="ep-btn-save"
              onClick={openEmailModal}
            >
              Change
            </button>

          </div>

        </div>

      </div>

      {/* ==========================================
          CHANGE MODAL
      ========================================== */}

      <Modal
        centered
        show={showModal}
        onHide={closeModal}
      >

        <Modal.Header closeButton>

          <Modal.Title>

            {step === 1
              ? `Change ${changeType === "mobile"
                ? "Mobile Number"
                : "Email"
              }`
              : "Verify OTP"}

          </Modal.Title>

        </Modal.Header>

        <Modal.Body>

          {/* ==========================================
              STEP 1
          ========================================== */}

          {step === 1 ? (

            <Form.Group>

              <Form.Label>

                {changeType === "mobile"
                  ? "New Mobile Number"
                  : "New Email Address"}

              </Form.Label>

              {changeType === "mobile" ? (

                <Form.Control
                  type="text"
                  maxLength={10}
                  placeholder="Enter new mobile number"
                  value={mobileNumber}
                  onChange={(e) =>
                    setMobileNumber(
                      e.target.value.replace(
                        /\D/g,
                        ""
                      )
                    )
                  }
                />

              ) : (

                <Form.Control
                  type="email"
                  placeholder="Enter new email address"
                  value={email}
                  onChange={(e) =>
                    setEmail(
                      e.target.value
                    )
                  }
                />

              )}

            </Form.Group>

          ) : (

            /* ==========================================
               STEP 2 - OTP
            ========================================== */

            <div className="text-center">

              <p className="text-muted mb-3">

                OTP sent to

                <br />

                <strong>
                  {changeType === "mobile"
                    ? mobileNumber
                    : email}
                </strong>

              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >

                {Array.from({
                  length: 6,
                }).map((_, index) => (

                  <input
                    key={index}
                    ref={(el) =>
                    (otpRefs.current[index] =
                      el)
                    }
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className="otp-input"
                    value={
                      otp[index] || ""
                    }
                    onChange={(e) =>
                      handleOtpChange(
                        e,
                        index
                      )
                    }
                    onKeyDown={(e) =>
                      handleOtpKeyDown(
                        e,
                        index
                      )
                    }
                  />

                ))}

              </div>

            </div>

          )}

        </Modal.Body>

        <Modal.Footer>

          <button
            className="ep-btn-cancel"
            onClick={closeModal}
            disabled={
              sending || verifying
            }
          >
            Cancel
          </button>

          {step === 1 ? (

            <button
              className="ep-btn-save"
              disabled={sending}
              onClick={handleSendOtp}
            >
              {sending
                ? "Sending..."
                : "Send OTP"}
            </button>

          ) : (

            <button
              className="ep-btn-save"
              disabled={verifying}
              onClick={handleVerifyOtp}
            >
              {verifying
                ? "Verifying..."
                : "Verify OTP"}
            </button>

          )}

        </Modal.Footer>

      </Modal>
    </>
  );
}